import { EraQL, Parser, Ty } from '@cotera/era';

export function outputShapeFromDefaultOutput(
  defaultOutput: unknown
): string | null {
  if (defaultOutput === null || defaultOutput === undefined) {
    return null;
  }
  const inferred = Parser.inferType([defaultOutput as Ty.Scalar]);
  if (inferred === undefined) {
    return null;
  }
  return Ty.displayTy(inferred);
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** The ERA type a JSON sample value implies, element by element. */
function tyFromSample(value: unknown): Ty.Shorthand {
  if (Array.isArray(value)) {
    // An empty array has no element to read a type off — `super`, so the array
    // is still an array (this is exactly what `Parser.inferType` can't do: it
    // returns undefined for a sample containing one, which left a dataset
    // trigger's `__meta.changedFields: []` untypable and the whole shape null).
    return value.length === 0
      ? Ty.arrayOf('super')
      : Ty.arrayOf(tyFromSample(value[0]));
  }
  if (value === null || value === undefined) {
    return 'super';
  }
  if (typeof value === 'object') {
    return Ty.structOf(
      Object.fromEntries(
        Object.entries(value).map(([key, child]) => [key, tyFromSample(child)])
      )
    );
  }
  if (typeof value === 'number') {
    return Number.isInteger(value) ? 'int' : 'float';
  }
  if (typeof value === 'boolean') {
    return 'boolean';
  }
  // A string stays `string`, not a single-value `ENUM('')` — the sample's value
  // is incidental, the field is a free string.
  return 'string';
}

/**
 * An ERA shape string inferred from a JSON sample, robustly where
 * {@link outputShapeFromDefaultOutput} (via `Parser.inferType`) gives up: empty
 * arrays and empty strings. Used to type a bucket's items from a trigger sample,
 * which has a sample but no shape resolver of its own.
 */
export function inferShapeFromSample(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  return Ty.displayTy(tyFromSample(value));
}

/** The string when it has non-whitespace content, otherwise `undefined`. */
export function nonBlank(value: string | null | undefined): string | undefined {
  return value !== null && value !== undefined && value.trim() !== ''
    ? value
    : undefined;
}

/** A shape string as an ERA type, falling back to `super` when absent/invalid. */
function shapeToTy(shape: string | null): Ty.Shorthand {
  if (shape === null) {
    return Ty.ty('super');
  }
  const parsed = EraQL.parseTy(shape);
  return parsed.isOk() ? parsed.value : Ty.ty('super');
}

/**
 * A for-each hands each downstream step an array of `{ input, output }` objects
 * (the item that went in paired with the loop body's result), so the output
 * shape mirrors that: `ARRAY(STRUCT({ input, output }))`. `input` stays generic
 * since item types vary across sources.
 */
export function displayForEachOutputShape(childShape: string | null): string {
  return Ty.displayTy(
    Ty.arrayOf(
      Ty.structOf({
        input: Ty.ty('super'),
        output: shapeToTy(childShape),
      })
    )
  );
}

/**
 * A bucket flushes a combined envelope to the step below it:
 * `{ items, count, reduced?, window }`. Mirrors `buildBucketFlushEnvelope` so the
 * field pickers on the steps below a bucket (a group-by's "list to split", most
 * commonly) offer exactly what the flush actually provides — `/items`, `/count`,
 * `/window/...` — instead of nothing.
 *
 * `items` carries `itemShape` as its element type when the bucket stores whole
 * contributions, and `super` otherwise (an `accumulate` pointer selecting a
 * sub-field, whose type we don't reconstruct here). `reduced` is present only
 * when the bucket declares reducers; each reduced value stays `super` since the
 * aggregation's result type varies by op.
 */
export function displayBucketFlushOutputShape(params: {
  readonly itemShape: string | null;
  readonly reducerKeys: readonly string[];
}): string {
  const fields: { [name: string]: Ty.Shorthand } = {
    items: Ty.arrayOf(shapeToTy(params.itemShape)),
    count: 'int',
  };
  if (params.reducerKeys.length > 0) {
    fields['reduced'] = Ty.structOf(
      Object.fromEntries(params.reducerKeys.map((key) => [key, Ty.ty('super')]))
    );
  }
  fields['window'] = Ty.structOf({
    from: 'string',
    to: 'string',
    flushedAt: 'string',
    reason: Ty.enumOf(['count', 'time', 'manual', 'group']),
  });
  return Ty.displayTy(Ty.structOf(fields));
}

/**
 * A group-by emits `{ groups, groupCount, itemCount }` (see `GroupByOutput`), so
 * the output shape mirrors that and the field pickers below it can offer
 * `/groups`, `/groups/0/key`, `/groups/0/items`, `/groups/0/count`,
 * `/groupCount` and `/itemCount` instead of one opaque "whole group-by output".
 *
 * The grouped item's own fields are left generic (`items: ARRAY(super)`), the
 * same way a bucket leaves its items generic when it can't resolve them and a
 * for-each leaves `input` generic. Deriving the element shape here would need the
 * predecessor's runtime sample — which the editor has (dataset columns and the
 * like) but the save-time validator does not — so a `/groups/0/items/0/<field>`
 * ref offered in the picker would then fail validation on save. Keeping it
 * generic keeps the two in step: per-item field work belongs in a for-each over
 * `/groups`, not an index into the group-by output. `key` is `super` too, since
 * the field the items were split on varies by source.
 */
export function displayGroupByOutputShape(): string {
  return Ty.displayTy(
    Ty.structOf({
      groups: Ty.arrayOf(
        Ty.structOf({
          key: Ty.ty('super'),
          items: Ty.arrayOf('super'),
          count: 'int',
        })
      ),
      groupCount: 'int',
      itemCount: 'int',
    })
  );
}
